/**
 * Live countdown to a contest milestone (submission deadline, judging
 * result, or opening). Auto-freezes at "0" once the moment passes.
 */
import { useEffect, useState } from "react";

const pad = (n) => String(Math.max(0, Math.floor(n))).padStart(2, "0");

function computeParts(targetIso) {
  const t = new Date(targetIso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  const seconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    done: false,
  };
}

export const ContestCountdown = ({
  targetIso,
  label = "Deadline",
  className = "",
  compact = false,
}) => {
  const [parts, setParts] = useState(() => computeParts(targetIso));

  useEffect(() => {
    if (!targetIso) return;
    setParts(computeParts(targetIso));
    const t = setInterval(() => setParts(computeParts(targetIso)), 1000);
    return () => clearInterval(t);
  }, [targetIso]);

  if (!targetIso || !parts) return null;

  const done = parts.done;
  const wrapper = compact
    ? "inline-flex items-center gap-2"
    : "flex flex-col items-start gap-1";

  return (
    <div className={`${wrapper} ${className}`} data-testid="contest-countdown">
      <span className="text-[10px] uppercase tracking-widest text-gray-400 font-black">
        {label}
      </span>
      {done ? (
        <span className="text-sm font-black text-amber-300" data-testid="contest-countdown-done">
          Time is up
        </span>
      ) : (
        <div className="flex items-baseline gap-1.5 font-mono tabular-nums text-white">
          <span className="text-lg sm:text-2xl font-black" data-testid="contest-countdown-days">{pad(parts.days)}d</span>
          <span className="text-lg sm:text-2xl font-black" data-testid="contest-countdown-hours">{pad(parts.hours)}h</span>
          <span className="text-lg sm:text-2xl font-black" data-testid="contest-countdown-minutes">{pad(parts.minutes)}m</span>
          <span className="text-lg sm:text-2xl font-black text-gray-400" data-testid="contest-countdown-seconds">{pad(parts.seconds)}s</span>
        </div>
      )}
    </div>
  );
};

export default ContestCountdown;
